import {
  DocumentOrganizationKeys,
  FilterDocumentDateFilterTypes,
} from '@griply/types';
import { dateFilterTypeTranslationKeys } from '@griply/utils';
import { TFunction } from 'i18next';
import { Option } from 'shared/components/ui/custom-form-select';
import { ActiveFilter, FilterGroup, FilterOption } from 'shared/types/filter';

const findOptionLabel = (
  options: Option[] | undefined,
  value: string,
): string | undefined => {
  if (!options?.length) {
    return undefined;
  }

  const direct = options.find((option) => option.value === value);
  if (direct) {
    return direct.label;
  }

  return options.reduce<string | undefined>(
    (found, option) => found ?? findOptionLabel(option.children, value),
    undefined,
  );
};

export type GetActiveFilterLabelsOptions = {
  activeOptions: ActiveFilter[];
  filters: FilterGroup[];
  t: TFunction;
};

// Default-valued filters are excluded so the trigger only summarises real choices.
export const getActiveFilterLabels = ({
  activeOptions,
  filters,
  t,
}: GetActiveFilterLabelsOptions): string[] => {
  const optionsById = new Map<string, FilterOption>();
  filters.forEach((group) =>
    group.filters.forEach((option) => optionsById.set(option.id, option)),
  );

  return activeOptions.reduce<string[]>((labels, active) => {
    if (
      active.id === DocumentOrganizationKeys.SortingMode ||
      active.id === DocumentOrganizationKeys.GroupByMode ||
      active.isDefault
    ) {
      return labels;
    }

    const option = optionsById.get(active.id);
    const selectOptions =
      option && 'selectOptions' in option ? option.selectOptions : undefined;

    active.value.forEach((value) => {
      if (!value) {
        return;
      }

      const dateKey =
        dateFilterTypeTranslationKeys[value as FilterDocumentDateFilterTypes];

      labels.push(
        findOptionLabel(selectOptions, value) ?? (dateKey ? t(dateKey) : value),
      );
    });

    return labels;
  }, []);
};
